// src/utils/imageUpload.js
//
// ПРОВЕРКИ КАРТИНКИ КЛИЕНТА ДЛЯ СТИКЕРОВ (п.89) — ОДИН ИСТОЧНИК.
//
// ═══════════════════════════════════════════════════════════════════
// 🔑 ПОЧЕМУ ОТДЕЛЬНЫЙ МОДУЛЬ, А НЕ КОПИЯ В КОМПОНЕНТЕ
//
//    Те же самые проверки нужны в ТРЁХ местах: в модалке карточки
//    («загружу свою картинку»), на шаге оформления заказа и в будущем
//    админском экране модерации. Три копии лимита — это три места,
//    где он разойдётся: ровно так в `LayoutUploader` появился
//    продублированный `MAX_SIZE` с предупреждением «правь ОБА».
//
// ⚠️ ЭТО НЕ ЗАЩИТА СЕРВЕРА. Фронт можно обойти: `accept` в браузере —
//    подсказка, проверку размера легко обойти консолью. Реальная защита —
//    на сервере (белый список + магические байты, см. `middleware/upload.js`).
//    Цель здесь — ЭКОНОМИЯ ВРЕМЕНИ клиента: не гнать 20 МБ по мобильному
//    интернету ради отказа в конце.
// ═══════════════════════════════════════════════════════════════════

// 🔴 ЛИМИТ — КОПИЯ СЕРВЕРНОГО `MAX_LAYOUT_SIZE` (20 МБ, решение владельца).
//    Источник истины — `backend/src/middleware/upload.js`. Меняешь там —
//    меняй здесь.
export const MAX_IMAGE_SIZE = 20 * 1024 * 1024;

// Растровые форматы для стикеров. 🔑 ПОЧЕМУ НЕ PDF/CDR:
//    картинку клиента обрабатывает плагин (убрать фон → кромка → макет),
//    а векторные макеты требуют другого пути (`LayoutUploader`, этапы 1.1–1.3).
//    Смешивать их в одном поле значило бы «иногда получается, иногда нет».
export const STICKER_IMAGE_EXT = ['.png', '.jpg', '.jpeg', '.webp', '.tif', '.tiff'];

export const STICKER_IMAGE_ACCEPT = STICKER_IMAGE_EXT.join(',');

/** Человекочитаемый размер. Байты клиенту ничего не говорят. */
export const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
};

/**
 * Проверить картинку ДО отправки.
 *
 * @returns {{ok: true} | {ok: false, error: string}}
 */
export const validateStickerImage = (file) => {
  if (!file) return { ok: false, error: 'Файл не выбран' };

  if (file.size > MAX_IMAGE_SIZE) {
    return {
      ok: false,
      error:
        `Файл слишком большой: ${formatSize(file.size)}. `
        + `Максимум — ${formatSize(MAX_IMAGE_SIZE)}. `
        + 'Уменьшите картинку или пришлите её нам — свяжемся и поможем.',
    };
  }

  const dot = file.name.lastIndexOf('.');
  const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
  if (!STICKER_IMAGE_EXT.includes(ext)) {
    return {
      ok: false,
      error: ext
        ? `Формат ${ext} не подходит. Нужна картинка: PNG, JPG, WEBP или TIFF.`
        : 'У файла нет расширения — не можем определить формат. '
          + 'Нужна картинка: PNG, JPG, WEBP или TIFF.',
    };
  }

  return { ok: true };
};

/**
 * 🔑 ПОДОЗРЕНИЕ НА БЕЛЫЙ ФОН / БЕЛЫЕ ДЕТАЛИ.
 *
 *    Белых чернил нет (STICKERS.md §6): белые детали картинки на белом
 *    фоне НЕ напечатаются. Это нельзя доказать без обработки пикселей,
 *    но можно предупредить заранее — до того, как клиент заплатит.
 *
 *    Проверяем не «фон белый», а «много белёсого по краям»: у многих
 *    картинок без вырезанного фона края именно светлые.
 *
 *    ⚠️ Возвращаем ТОЛЬКО предупреждение, никогда не блокируем: это
 *    догадка по эвристике, а не факт. Блокировать по догадке —
 *    отказывать клиенту в продаже без основания.
 *
 * @returns {Promise<string|null>} текст предупреждения или `null`.
 */
export const checkLightEdges = (file) => new Promise((resolve) => {
  try {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const done = (result) => {
      URL.revokeObjectURL(url);
      resolve(result);
    };

    img.onload = () => {
      try {
        const S = 64; // достаточно мелко, чтобы не тормозить, и крупно, чтобы мерить
        const canvas = document.createElement('canvas');
        canvas.width = S;
        canvas.height = S;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, S, S);
        const { data } = ctx.getImageData(0, 0, S, S);

        let checked = 0;
        let light = 0;
        // Только ПЕРИМЕТР кадра (2 пикселя по краям): середина картинки
        // бывает светлой совершенно законно (облако, блик, текст).
        for (let y = 0; y < S; y++) {
          for (let x = 0; x < S; x++) {
            const isEdge = x < 2 || y < 2 || x >= S - 2 || y >= S - 2;
            if (!isEdge) continue;
            const i = (y * S + x) * 4;
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];
            // Прозрачные пиксели не белые — это уже вырезанный фон.
            if (a < 32) { checked++; continue; }
            checked++;
            if (r > 240 && g > 240 && b > 240) light++;
          }
        }

        const share = checked ? light / checked : 0;
        done(
          share > 0.55
            ? 'Похоже, у картинки белый фон или белые края. Белой краски мы '
              + 'не печатаем: такие детали останутся прозрачными. Это '
              + 'бывает нормально — просто проверьте, как выглядит макет.'
            : null
        );
      } catch {
        // CORS/тайнит канваса или битая картинка — предупреждения нет.
        done(null);
      }
    };
    img.onerror = () => done(null);
    img.src = url;
  } catch {
    resolve(null);
  }
});
